import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import orderBy from "lodash/orderBy";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import InfiniteScroll from "~/components/VirtualList/InfiniteScroll";
import useActivityAnchorScroll from "~/hooks/lead/useActivityAnchorScroll";
import dayjs from "~/lib/dayjs";
import Activity from "./Activity";
import HistoryToggleButton from "./HistoryToggleButton";
import SubmissionActivityEntry from "./SubmissionActivityEntry";

const SUBMISSION_ENTRY = { isSubmissionEntry: true };
const getActivityItemKey = (activity) =>
  activity?.isSubmissionEntry ? "submission-entry" : activity?.id;

const Activities = ({
  loadingActivities,
  loadingPrevActivities = false,
  activities,
  activityId = null,
  activeTab,
  nextPage = null,
  prevPage = null,
  loadMoreActivities,
  hideOlderThan,
  lead,
  setCurrentFormWithData,
  toggleActivityInternal,
  removeActivityFromList,
  buildPinProps = null,
}) => {
  const [showAfterActivities, setShowAfterActivities] = useState(true);

  const validActivities = useMemo(
    () => activities.filter((activity) => activity !== null),
    [activities],
  );

  const { sortedActivities, beforeActivities, afterActivities } =
    useMemo(() => {
      const sorted = orderBy(validActivities, ["occurred_at"], "desc");
      const before = sorted.filter((activity) =>
        dayjs(activity.occurred_at).isBefore(hideOlderThan),
      );
      const after = sorted.filter((activity) =>
        dayjs(activity.occurred_at).isAfter(hideOlderThan),
      );

      const submissionEntry =
        activeTab === "all" || activeTab === "changes"
          ? [SUBMISSION_ENTRY]
          : [];

      return {
        sortedActivities: [...sorted, ...submissionEntry],
        beforeActivities: before,
        afterActivities: after,
      };
    }, [validActivities, hideOlderThan, activeTab]);

  const {
    infiniteScrollRef,
    activitiesContainerRef,
    highlightedActivityId,
    isHighlightFading,
    handleItemsRendered,
    shouldPauseAutoNextPage,
  } = useActivityAnchorScroll({
    activityId,
    leadId: lead.id,
    sortedActivities,
    loadingActivities,
    prevPage,
    nextPage,
    loadMoreActivities,
  });

  const renderLoading = () => (
    <div className="text-center mt-3">
      <FontAwesomeIcon icon="far fa-spinner" pulse />
    </div>
  );

  const renderNoActivities = () => {
    if (
      !loadingActivities &&
      activities.length === 0 &&
      activeTab !== "all" &&
      activeTab !== "changes"
    ) {
      return (
        <div className="text-center mt-3">
          <p>No activities found for this tab.</p>
        </div>
      );
    }
    return null;
  };

  const renderActivityEntries = useCallback(
    (activity, index) => {
      if (!activity) {
        return null;
      }

      if (activity.isSubmissionEntry) {
        return <SubmissionActivityEntry key="submission-entry" lead={lead} />;
      }

      const pinProps = buildPinProps ? buildPinProps(activity) : {};

      return (
        <Activity
          key={`activity-${activity?.id || index}`}
          activity={activity}
          activityId={activityId}
          activeTab={activeTab}
          lead={lead}
          setCurrentFormWithData={setCurrentFormWithData}
          toggleActivityInternal={toggleActivityInternal}
          removeActivity={removeActivityFromList}
          isHighlighted={activity?.id === highlightedActivityId}
          isHighlightFading={
            activity?.id === highlightedActivityId && isHighlightFading
          }
          {...pinProps}
        />
      );
    },
    [
      lead,
      activityId,
      activeTab,
      setCurrentFormWithData,
      toggleActivityInternal,
      removeActivityFromList,
      highlightedActivityId,
      buildPinProps,
    ],
  );

  const renderInfiniteScroll = (items) => (
    <InfiniteScroll
      ref={infiniteScrollRef}
      items={items}
      renderItem={renderActivityEntries}
      itemSize={100}
      nextPage={shouldPauseAutoNextPage ? null : nextPage}
      prevPage={prevPage}
      isNextPageLoading={loadingActivities}
      isPrevPageLoading={loadingPrevActivities}
      loadNextPage={loadMoreActivities}
      onItemsRendered={handleItemsRendered}
      getItemKey={getActivityItemKey}
    />
  );

  const toggleAfterActivities = () => {
    setShowAfterActivities(!showAfterActivities);
  };

  const combinedActivities = useMemo(() => {
    const baseActivities = showAfterActivities
      ? [...afterActivities, ...beforeActivities]
      : [...afterActivities];

    const submissionEntry =
      activeTab === "all" || activeTab === "changes" ? [SUBMISSION_ENTRY] : [];

    return [...baseActivities, ...submissionEntry];
  }, [showAfterActivities, afterActivities, beforeActivities, activeTab]);

  const renderContent = useMemo(
    () =>
      ({ length, items }) => (
        <>{length > 0 ? renderInfiniteScroll(items) : renderNoActivities()}</>
      ),
    [renderInfiniteScroll, renderNoActivities],
  );

  const productType =
    typeof GlobalContainer.product === "function"
      ? GlobalContainer.product()
      : "recruiting";

  if (loadingActivities && activities.length === 0) {
    return renderLoading();
  }

  if (productType === "retention") {
    return (
      <>
        <div className="text-center mt-2">
          <HistoryToggleButton
            onClick={toggleAfterActivities}
            isHidden={!showAfterActivities}
            count={beforeActivities.length}
          />
        </div>
        <div
          ref={activitiesContainerRef}
          className="mt-4 lead-drawer-activities-fill"
        >
          {renderContent({
            length: combinedActivities.length,
            items: combinedActivities,
          })}
        </div>
      </>
    );
  }

  return (
    <>
      <div
        ref={activitiesContainerRef}
        className="mt-4 lead-drawer-activities-fill"
      >
        {renderContent({
          length: sortedActivities.length,
          items: sortedActivities,
        })}
      </div>
    </>
  );
};

Activities.propTypes = {
  loadingActivities: PropTypes.bool.isRequired,
  loadingPrevActivities: PropTypes.bool,
  activities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  activityId: PropTypes.number,
  activeTab: PropTypes.string.isRequired,
  nextPage: PropTypes.number,
  prevPage: PropTypes.number,
  loadMoreActivities: PropTypes.func.isRequired,
  hideOlderThan: PropTypes.instanceOf(Date).isRequired,
  lead: PropTypes.shape({}).isRequired,
  setCurrentFormWithData: PropTypes.func.isRequired,
  toggleActivityInternal: PropTypes.func.isRequired,
  removeActivityFromList: PropTypes.func.isRequired,
};

export default Activities;
